import { useEffect, useMemo, useRef, useState } from 'react'
import { useInView } from 'framer-motion'
import { FileText, Layers, Users, Euro } from 'lucide-react'

const COUNT_UP_MS = 2000

const labels = {
  el: { offers: 'Προσφορές', batches: 'Παρτίδες', clients: 'Πελάτες', revenueEur: 'Συνολική Αξία Προσφορών' },
  en: { offers: 'Offers Created', batches: 'Batches Created', clients: 'Total Clients', revenueEur: 'Total Value Quoted' },
}

const items = [
  { key: 'offers', icon: FileText },
  { key: 'batches', icon: Layers },
  { key: 'clients', icon: Users },
  { key: 'revenueEur', icon: Euro, prefix: '€' },
]

// Counts every value from 0 up to its target with an ease-out curve, starting
// once `start` turns true. Re-runs on every mount, so each homepage visit
// replays the animation.
function useCountUp(targets, start) {
  const [values, setValues] = useState({})
  useEffect(() => {
    if (!start || !targets) return
    let frame
    const t0 = performance.now()
    const tick = (now) => {
      const p = Math.min((now - t0) / COUNT_UP_MS, 1)
      const eased = 1 - Math.pow(1 - p, 3)
      const next = {}
      for (const key of Object.keys(targets)) next[key] = Math.round(targets[key] * eased)
      setValues(next)
      if (p < 1) frame = requestAnimationFrame(tick)
    }
    frame = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(frame)
  }, [targets, start])
  return values
}

// Fetches the public usage totals once per mount; null until they arrive.
export function useLiveStats() {
  const [stats, setStats] = useState(null)
  useEffect(() => {
    fetch(`${import.meta.env.BASE_URL}api/stats/public`)
      .then((res) => (res.ok ? res.json() : null))
      .then((data) => data && setStats(data))
      .catch(() => {})
  }, [])
  return stats
}

// A single number counting up from 0 to `value` as soon as it is known.
export const CountUp = ({ value, lang }) => {
  const targets = useMemo(() => (value == null ? null : { value }), [value])
  const { value: current = 0 } = useCountUp(targets, true)
  return current.toLocaleString(lang === 'el' ? 'el-GR' : 'en-US')
}

const LiveStats = ({ stats, lang }) => {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, amount: 0.4 })
  const values = useCountUp(stats, inView)
  const t = labels[lang] || labels.en
  const locale = lang === 'el' ? 'el-GR' : 'en-US'

  return (
    <section ref={ref} className="bg-primary-600 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
          {items.map(({ key, icon: Icon, prefix = '' }) => (
            <div key={key} className="flex flex-col items-center text-center">
              <Icon className="h-7 w-7 mb-2 text-primary-200" />
              <div className="text-3xl md:text-4xl font-bold tabular-nums">
                {prefix}{(values[key] || 0).toLocaleString(locale)}
              </div>
              <div className="text-sm md:text-base text-primary-100 mt-1">{t[key]}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

export default LiveStats
